import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { BrandLogoUpload } from "./BrandLogoUpload";
import { theme } from "../../theme";
import { getFileUrl } from "@/utility/utils";
import { SessionContext } from "@/utility/useSession";
import { type Brand } from "@/logic/api";

interface AddEditBrandModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (brand: Omit<Brand, "id">, logoFile?: File) => Promise<void>;
    brand?: Brand;
    isLoading?: boolean;
}

export const AddEditBrandModal: React.FC<AddEditBrandModalProps> = ({
    open,
    onClose,
    onSave,
    brand,
    isLoading = false
}) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        brandCode: "",
        displayName: "",
        default: false
    });
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [saveError, setSaveError] = useState<string>("");
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (open) {
            setIsEditMode(Boolean(brand?.id));
            if (brand) {
                setFormData({
                    brandCode: brand.brandCode || "",
                    displayName: brand.displayName || "",
                    default: brand.default || false
                });
            } else {
                setFormData({
                    brandCode: "",
                    displayName: "",
                    default: false
                });
            }
            setLogoFile(null);
            setRemoveLogo(false);
            setErrors({});
            setSaveError("");
        }
    }, [open, brand]);

    const hasChanges = open && (
        !isEditMode || // this is for new brand as it always allows save
        formData.brandCode !== (brand?.brandCode || "") ||
        formData.displayName !== (brand?.displayName || "") ||
        formData.default !== (brand?.default || false) ||
        logoFile !== null ||
        removeLogo
    );

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.brandCode.trim()) {
            newErrors.brandCode = t("brand.validations.brandCodeRequired");
        } else if (formData.brandCode.length > 255) {
            newErrors.brandCode = t("brand.validations.brandCodeLength");
        }

        if (!formData.displayName.trim()) {
            newErrors.displayName = t("brand.validations.displayNameRequired");
        } else if (formData.displayName.length > 255) {
            newErrors.displayName = t("brand.validations.displayNameLength");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaveError("");
            await onSave(
                {
                    brandCode: formData.brandCode.trim(),
                    displayName: formData.displayName.trim(),
                    default: formData.default,
                    active: true,
                    fileName: (removeLogo ? "" : (brand?.fileName || ""))
                },
                logoFile || undefined
            );
            onClose();
        } catch (error) {
            console.error("Error saving brand:", error);
            setSaveError(error instanceof Error ? error.message : "Failed to save brand");
        }
    };

    const handleFieldChange = (field: keyof typeof formData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // we need this to clear the error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const getCurrentImageUrl = () => {
        if (logoFile) {
            return URL.createObjectURL(logoFile);
        }
        if (brand?.fileName && !removeLogo) {
            return getFileUrl(session, brand.fileName);
        }
        return undefined;
    };

    const handleLogoChange = (file: File | null) => {
        if (file) {
            setLogoFile(file);
            setRemoveLogo(false);
        } else {
            setLogoFile(null);
            setRemoveLogo(true);
        }
    };

    const currentImage = getCurrentImageUrl() ? {
        url: getCurrentImageUrl()!,
        name: logoFile?.name || t("brand.upload.currentLogo")
    } : undefined;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            data-testid="add-edit-brand-modal"
        >
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
                        {isEditMode ? t("brand.management.editBrand") : t("brand.management.addNewBrand")}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {saveError}
                    </Alert>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            {t("brand.fields.brandCode")} <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={t("brand.fields.brandCodePlaceholder")}
                            value={formData.brandCode}
                            onChange={(e) => handleFieldChange("brandCode", e.target.value)}
                            error={Boolean(errors.brandCode)}
                            helperText={errors.brandCode || t("brand.fields.brandCodeHelper")}
                            disabled={isLoading}
                            data-testid="brand-value-input"
                            size="small"
                            variant="outlined"
                            autoComplete="off"
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            {t("brand.fields.displayName")} <span style={{ color: theme.palette.error.main }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={t("brand.fields.displayNamePlaceholder")}
                            value={formData.displayName}
                            onChange={(e) => handleFieldChange("displayName", e.target.value)}
                            error={Boolean(errors.displayName)}
                            helperText={errors.displayName || t("brand.fields.displayNameHelper")}
                            disabled={isLoading}
                            data-testid="brand-display-name-input"
                            size="small"
                            variant="outlined"
                            autoComplete="off"
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                            {t("brand.fields.brandLogo")}
                        </Typography>
                        <BrandLogoUpload
                            onFileSelected={handleLogoChange}
                            currentImage={currentImage}
                            disabled={isLoading}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    variant="text"
                    disabled={isLoading}
                    sx={{
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.action.hover,
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                    }}
                    disableRipple
                    disableElevation
                    data-testid="cancel-brand-button"
                >
                    {t("brand.actions.cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={isLoading || (isEditMode && !hasChanges)}
                    sx={{
                        fontWeight: "bold",
                        backgroundColor: (theme) => theme.palette.brand.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1), box-shadow 150ms ease",
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.brand.dark,
                            color: (theme) => theme.palette.primary.contrastText,
                        },
                        "&:active": {
                            transform: "scale(0.97)",
                        },
                        "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
                        },
                        "&:disabled": {
                            backgroundColor: "#ccc",
                            color: "#666"
                        }
                    }}
                    disableRipple
                    disableElevation
                    data-testid="save-brand-button"
                >
                    {isLoading ? t("brand.actions.saving") : (isEditMode ? t("brand.actions.updateBrand") : t("brand.actions.addBrand"))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};